import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  IconPlus,
  IconTrash,
  IconStar,
  IconStarFilled,
} from '@tabler/icons-react';
import type {
  QuoteTermsTemplate,
  QuoteTermsTemplateCreate,
  QuoteTermsTemplateUpdate,
  TermsBodyFormat,
} from '@techgitter/contracts';
import {
  QUOTE_TEMPLATE_VARIABLES,
  TERMS_BODY_FORMAT_DEFAULT,
  normaliseTermsBodyFormat,
} from '@techgitter/contracts';

import {
  invalidateQuoteTermsTemplatesCache,
  useQuoteTermsTemplates,
} from '../hooks/useQuoteTermsTemplates';
import {
  createQuoteTermsTemplate,
  deleteQuoteTermsTemplate,
  setDefaultQuoteTermsTemplate,
  updateQuoteTermsTemplate,
} from '../services/quoteTermsTemplatesApi';
import { useAppSettings } from '../hooks/useAppSettings';
import { renderQuoteTemplate } from '../utils/quoteTemplateRenderer';
import { TermsMarkdown } from '../components/common/TermsMarkdown';
import {
  TermsFormatToggle,
  UnknownVariableWarning,
  VariablesPanel,
  insertAtCursor,
} from '../components/admin/TermsEditorControls';
import {
  SecondaryToolbar,
  ToolbarButton,
  ToolbarCount,
  ToolbarSpacer,
} from '../components/toolbar';
import { Chip } from '../components/chip';
import {
  CARD_SX,
  FIELD_SX,
  PRIMARY_BTN_SX,
} from '../styles/designSystem';

/**
 * T&C template editor.
 *
 * Left rail: template list with "+ New" + default indicator.
 * Right pane: editor (name / slug / body / closer) + live preview
 *             interpolating against synthetic quote data + current
 *             AppSettings.
 *
 * Writes go through the row-level Dataverse API. The default-flag
 * invariant (exactly one default) is enforced server-side; the page
 * just calls ``set-default`` and refetches.
 *
 * Card-on-grey design system (sm4rt-web-ui skill): a floating toolbar over
 * a grey surface, CARD_SX cards for the list rail / editor / preview /
 * variables, shared Chip badges and Tabler icons.
 */
export const QuoteTermsTemplatesAdminPage: React.FC = () => {
  const { templates, isLoading, error } = useQuoteTermsTemplates();
  const { settings } = useAppSettings();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditorDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<QuoteTermsTemplate | null>(
    null,
  );
  const [newDialog, setNewDialog] = useState<NewDialogState | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Default-select the default template (or the first) when the
  // catalog lands; preserve the tech's current pick when the list
  // refetches after a save.
  useEffect(() => {
    if (!templates.length) {
      setSelectedId(null);
      setDraft(null);
      return;
    }
    if (selectedId && templates.some((t) => t.id === selectedId)) {
      const t = templates.find((x) => x.id === selectedId)!;
      setDraft(toDraft(t));
      return;
    }
    const target = templates.find((t) => t.isDefault) || templates[0];
    setSelectedId(target.id);
    setDraft(toDraft(target));
  }, [templates, selectedId]);

  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId],
  );

  const dirty = useMemo(() => {
    if (!draft || !selected) return false;
    return (
      draft.name !== selected.name ||
      (draft.description ?? '') !== (selected.description ?? '') ||
      draft.body !== selected.body ||
      (draft.closer ?? '') !== (selected.closer ?? '')
    );
  }, [draft, selected]);

  const handleSelect = (id: string) => {
    if (dirty) {
      const confirm = window.confirm(
        'Unsaved changes will be discarded. Continue?',
      );
      if (!confirm) return;
    }
    setSelectedId(id);
    const t = templates.find((x) => x.id === id);
    setDraft(t ? toDraft(t) : null);
  };

  // The variables panel inserts at the caret, so it needs the live textarea.
  const bodyFieldRef = React.useRef<HTMLTextAreaElement | null>(null);

  const handleSave = async () => {
    if (!selected || !draft || !dirty) return;
    setSaving(true);
    setSaveError(null);
    const patch: QuoteTermsTemplateUpdate = {
      name: draft.name,
      description: draft.description || undefined,
      body: draft.body,
      closer: draft.closer || undefined,
      bodyFormat: draft.bodyFormat,
    };
    try {
      await updateQuoteTermsTemplate(selected.id, patch);
      invalidateQuoteTermsTemplatesCache();
      setSnackbar({
        open: true,
        message: 'Template saved',
        severity: 'success',
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async (payload: QuoteTermsTemplateCreate) => {
    setSaving(true);
    setSaveError(null);
    try {
      const created = await createQuoteTermsTemplate(payload);
      invalidateQuoteTermsTemplatesCache();
      setSelectedId(created.id);
      setNewDialog(null);
      setSnackbar({
        open: true,
        message: 'Template created',
        severity: 'success',
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (id: string) => {
    setSaving(true);
    setSaveError(null);
    try {
      await setDefaultQuoteTermsTemplate(id);
      invalidateQuoteTermsTemplatesCache();
      setSnackbar({
        open: true,
        message: 'Default updated',
        severity: 'success',
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setSaving(true);
    setSaveError(null);
    try {
      await deleteQuoteTermsTemplate(confirmDelete.id);
      invalidateQuoteTermsTemplatesCache();
      if (selectedId === confirmDelete.id) setSelectedId(null);
      setConfirmDelete(null);
      setSnackbar({
        open: true,
        message: 'Template deleted',
        severity: 'success',
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SecondaryToolbar floating>
        <ToolbarCount>
          {templates.length} template{templates.length === 1 ? '' : 's'}
        </ToolbarCount>
        <ToolbarSpacer />
        <ToolbarButton
          variant="primary"
          icon={IconPlus}
          onClick={() =>
            setNewDialog({
              slug: '',
              name: '',
              body: '',
              bodyFormat: TERMS_BODY_FORMAT_DEFAULT,
            })
          }
        >
          New template
        </ToolbarButton>
      </SecondaryToolbar>

      <Box sx={{ p: '12px 16px 24px' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Box sx={[CARD_SX, { p: 3, maxWidth: 720 }]}>
            <Typography sx={{ fontSize: 13, color: '#666' }}>
              No templates yet — click <strong>New template</strong> to create
              one. Quote previews fall back to a hardcoded default until at
              least one template is created and marked as the default.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            {/* Left rail — template list */}
            <Box sx={[CARD_SX, { width: 260, flexShrink: 0, overflow: 'hidden', p: 0.5 }]}>
              {templates.map((t) => {
                const active = t.id === selectedId;
                return (
                  <Box
                    key={t.id}
                    onClick={() => handleSelect(t.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      px: 1.25,
                      py: 1,
                      borderRadius: '6px',
                      cursor: 'pointer',
                      bgcolor: active ? '#E6F1FB' : 'transparent',
                      '&:hover': { bgcolor: active ? '#E6F1FB' : '#f7f9fc' },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: active ? 500 : 400,
                          color: active ? '#0C447C' : '#1a1a1a',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {t.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: '#888',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {t.slug}
                      </Typography>
                    </Box>
                    {t.isDefault && (
                      <Tooltip title="Default template">
                        <Box
                          component="span"
                          sx={{ color: '#EF9F27', display: 'inline-flex', flexShrink: 0 }}
                        >
                          <IconStarFilled size={13} />
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Right pane — editor */}
            {draft && selected ? (
              <Box
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Box sx={[CARD_SX, { p: 2 }]}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    <TextField
                      label="Name"
                      size="small"
                      value={draft.name}
                      onChange={(e) =>
                        setDraft({ ...draft, name: e.target.value })
                      }
                      sx={[FIELD_SX, { flexGrow: 1 }]}
                    />
                    <Chip tone="neutral">slug: {selected.slug}</Chip>
                    <Tooltip
                      title={
                        selected.isDefault
                          ? 'Currently the default template'
                          : 'Mark as default'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={selected.isDefault || saving}
                          onClick={() => handleSetDefault(selected.id)}
                          sx={{ color: selected.isDefault ? '#EF9F27' : '#bbb' }}
                        >
                          {selected.isDefault ? (
                            <IconStarFilled size={17} />
                          ) : (
                            <IconStar size={17} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        selected.isDefault
                          ? 'Cannot delete the default — mark another template as default first.'
                          : 'Delete template'
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={selected.isDefault || saving}
                          onClick={() => setConfirmDelete(selected)}
                          sx={{
                            color: '#bbb',
                            '&:hover': { color: '#B03A2E' },
                          }}
                        >
                          <IconTrash size={17} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      label="Description (admin only — not shown to customers)"
                      size="small"
                      value={draft.description ?? ''}
                      onChange={(e) =>
                        setDraft({ ...draft, description: e.target.value })
                      }
                      sx={FIELD_SX}
                    />
                    <TermsFormatToggle
                      value={draft.bodyFormat}
                      onChange={(bodyFormat) => setDraft({ ...draft, bodyFormat })}
                      warnOnSwitch={
                        draft.bodyFormat !==
                        normaliseTermsBodyFormat(selected.bodyFormat)
                      }
                    />
                    <UnknownVariableWarning
                      sources={[draft.body, draft.closer]}
                      variables={QUOTE_TEMPLATE_VARIABLES}
                    />
                    <TextField
                      label="Body"
                      multiline
                      minRows={8}
                      value={draft.body}
                      inputRef={bodyFieldRef}
                      onChange={(e) =>
                        setDraft({ ...draft, body: e.target.value })
                      }
                      helperText="Supports {{path.to.value}} and {{#if path}}…{{/if}} — see the variables panel."
                      sx={FIELD_SX}
                    />
                    <TextField
                      label="Closer (optional friendly sign-off)"
                      multiline
                      minRows={2}
                      value={draft.closer ?? ''}
                      onChange={(e) =>
                        setDraft({ ...draft, closer: e.target.value })
                      }
                      sx={FIELD_SX}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!dirty || saving}
                        sx={PRIMARY_BTN_SX}
                      >
                        {saving ? 'Saving…' : 'Save'}
                      </Button>
                    </Box>
                  </Box>
                </Box>

                <PreviewPanel draft={draft} appSettings={settings} />

                <VariablesPanel
                  variables={QUOTE_TEMPLATE_VARIABLES}
                  groups={['appSettings', 'quote', 'customer', 'system']}
                  onInsert={(snippet) =>
                    setDraft({
                      ...draft,
                      body: insertAtCursor(
                        bodyFieldRef.current,
                        draft.body,
                        snippet,
                      ),
                    })
                  }
                />
              </Box>
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography sx={{ fontSize: 13, color: '#888' }}>
                  Select a template from the list to edit.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {confirmDelete && (
        <Dialog open onClose={() => setConfirmDelete(null)}>
          <DialogTitle>Delete template?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Delete <strong>{confirmDelete.name}</strong>? Quotes that
              reference this template fall back to the default at render
              time. The template is soft-deleted — slug stays reserved.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button sx={CANCEL_BTN_SX} onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button color="error" onClick={handleDelete} disabled={saving}>
              {saving ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {newDialog && (
        <NewTemplateDialog
          state={newDialog}
          onChange={setNewDialog}
          onClose={() => setNewDialog(null)}
          onCreate={handleCreate}
          saving={saving}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

/** Recessive grey Cancel/Back button (sm4rt-web-ui skill §9 — never blue). */
const CANCEL_BTN_SX = {
  color: '#888',
  '&:hover': { color: '#555', bgcolor: 'transparent' },
};

interface EditorDraft {
  name: string;
  description?: string;
  body: string;
  closer?: string;
  bodyFormat: TermsBodyFormat;
}

function toDraft(t: QuoteTermsTemplate): EditorDraft {
  return {
    name: t.name,
    description: t.description,
    body: t.body,
    closer: t.closer,
    // Normalised, not read raw: an environment where the Dataverse column has
    // not been created yet returns undefined, which means 'text'.
    bodyFormat: normaliseTermsBodyFormat(t.bodyFormat),
  };
}

interface PreviewPanelProps {
  draft: EditorDraft;
  appSettings: import('@techgitter/contracts').AppSettings | null;
}

const SAMPLE_QUOTE: import('@techgitter/contracts').QuoteDetail = {
  id: 'sample',
  number: 'Q-SAMPLE',
  name: 'Sample Quote',
  status: 'draft',
  type: 'new_laptop',
  consolidated: false,
  consolidatedDescription: null,
  validUntil: '2026-06-19',
  note: null,
  customerId: null,
  customerName: 'Acme Pty Ltd',
  customerBusinessName: 'Acme Pty Ltd',
  customerFullName: 'Jane Smith',
  customerPhone: '(02) 9000 1234',
  customerMobile: '0412 345 678',
  customerEmail: 'jane@acme.com.au',
  rsTicketId: null,
  rsTicketNumber: '18422',
  sm4rtJobId: null,
  invoiceId: null,
  invoiceNumber: null,
  invoicePaid: null,
  promotedAt: null,
  partOrderRaised: false,
  total: 1234.56,
  priceDisplayMode: 'ex',
  markup: 0.115,
  deviceType: null,
  sourceCheckInDraftJobId: null,
  bundleDiscount: null,
  includeEftFee: true,
  eftFeeRate: 0.011,
  termsTemplateId: null,
  variationGroupId: null,
  ticketGeneratedAt: null,
  diagnosticReimbursement: null,
  createdAt: null,
  updatedAt: null,
  items: [],
  repairServices: [],
};

const PreviewPanel: React.FC<PreviewPanelProps> = ({ draft, appSettings }) => {
  const body = renderQuoteTemplate(draft.body, {
    appSettings,
    quote: SAMPLE_QUOTE,
  });
  const closer = renderQuoteTemplate(draft.closer ?? '', {
    appSettings,
    quote: SAMPLE_QUOTE,
  });
  return (
    <Box sx={[CARD_SX, { p: 2 }]}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a', mb: 1 }}>
        Live preview (against sample quote data)
      </Typography>
      <Box sx={{ borderTop: '0.5px solid #e2eaf3', mb: 1.5 }} />
      {/* Rendered, not raw: the point of the preview is to show what the
          markdown does before a customer is the first one to see it. */}
      <TermsMarkdown
        body={body}
        format={draft.bodyFormat}
        sx={{ fontSize: 13, color: '#666' }}
      />
      {closer && (
        <TermsMarkdown
          body={closer}
          format={draft.bodyFormat}
          sx={{ fontSize: 13, color: '#333', mt: 1.5 }}
        />
      )}
    </Box>
  );
};

// ---------------------------------------------------------------------------
// New-template dialog
// ---------------------------------------------------------------------------

interface NewDialogState {
  slug: string;
  name: string;
  body: string;
  description?: string;
  closer?: string;
  bodyFormat: TermsBodyFormat;
  isDefault?: boolean;
}

interface NewTemplateDialogProps {
  state: NewDialogState;
  onChange: (s: NewDialogState) => void;
  onClose: () => void;
  onCreate: (payload: QuoteTermsTemplateCreate) => void;
  saving: boolean;
}

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);

const NewTemplateDialog: React.FC<NewTemplateDialogProps> = ({
  state,
  onChange,
  onClose,
  onCreate,
  saving,
}) => {
  const canCreate =
    state.slug.trim() !== '' && state.name.trim() !== '' && state.body.trim() !== '';
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New template</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          <TextField
            label="Name"
            size="small"
            value={state.name}
            sx={FIELD_SX}
            onChange={(e) => {
              const name = e.target.value;
              // Auto-suggest slug from name until the tech edits it
              const suggested = slugify(name);
              onChange({
                ...state,
                name,
                slug:
                  state.slug === '' || state.slug === slugify(state.name)
                    ? suggested
                    : state.slug,
              });
            }}
          />
          <TextField
            label="Slug"
            size="small"
            value={state.slug}
            sx={FIELD_SX}
            onChange={(e) =>
              onChange({ ...state, slug: slugify(e.target.value) })
            }
            helperText="Stable identifier referenced by quotes. Lowercase letters, digits, dash, underscore."
          />
          <TextField
            label="Description (admin only)"
            size="small"
            value={state.description ?? ''}
            sx={FIELD_SX}
            onChange={(e) =>
              onChange({ ...state, description: e.target.value })
            }
          />
          <TermsFormatToggle
            value={state.bodyFormat}
            onChange={(bodyFormat) => onChange({ ...state, bodyFormat })}
          />
          <TextField
            label="Body"
            multiline
            minRows={6}
            value={state.body}
            sx={FIELD_SX}
            onChange={(e) => onChange({ ...state, body: e.target.value })}
          />
          <TextField
            label="Closer"
            multiline
            minRows={2}
            value={state.closer ?? ''}
            sx={FIELD_SX}
            onChange={(e) => onChange({ ...state, closer: e.target.value })}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Switch
              checked={Boolean(state.isDefault)}
              onChange={(e) =>
                onChange({ ...state, isDefault: e.target.checked })
              }
            />
            <Typography sx={{ fontSize: 13 }}>Mark as default</Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button sx={CANCEL_BTN_SX} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={PRIMARY_BTN_SX}
          onClick={() =>
            onCreate({
              slug: state.slug,
              name: state.name,
              body: state.body,
              description: state.description || undefined,
              closer: state.closer || undefined,
              bodyFormat: state.bodyFormat,
              isDefault: state.isDefault,
            })
          }
          disabled={!canCreate || saving}
        >
          {saving ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
